import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  IconButton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { offerApi, OfferGroupDetail } from '@texas/api/endpoints/offerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { Locale } from '@texas/i18n/types';
import { ServerError } from '@texas/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { TFunction } from 'i18next';
import { ReactNode } from 'react';
import { EditOfferGroupDrawer } from './EditOfferGroupDrawer';
import { offerEvents } from './events';

interface Props {
  group: OfferGroupDetail;
  tierTags: ReactNode;
  loading: boolean;
  error: ServerError | null;
  onUpdated: () => void;
  onMakeOffer: () => void;
  t: TFunction;
}

export function OfferGroupSummaryCard({
  group,
  tierTags,
  loading,
  error,
  onUpdated,
  onMakeOffer,
  t,
}: Props) {
  const { request: archiveGroup, loading: archiving } = useApiRequest(
    offerApi.archiveGroup,
  );
  const { request: restoreGroup, loading: restoring } = useApiRequest(
    offerApi.restoreGroup,
  );

  const handleArchive = async () => {
    const res = await archiveGroup(group.id);
    if (!res.hasError) {
      onUpdated();
      offerEvents.onUpdated.dispatch();
    }
  };

  const handleRestore = async () => {
    const res = await restoreGroup(group.id);
    if (!res.hasError) {
      onUpdated();
      offerEvents.onUpdated.dispatch();
    }
  };

  return (
    <Card
      variant="dark"
      mb={6}
      opacity={group.archived ? 0.7 : 1}
      borderColor={group.archived ? 'orange.500' : undefined}
      _light={{ borderColor: group.archived ? 'orange.300' : undefined }}
    >
      <CardBody>
        <Flex justify="space-between" align="flex-start">
          <Box>
            {error && <ErrorDetails error={error} />}
            <Flex align="center" gap={2} mb={1}>
              {loading && <Spinner size="sm" />}
              <Text fontWeight="bold" fontSize="lg">
                {t('offer.offer')}
              </Text>
              {group.archived && (
                <Badge colorScheme="orange" fontSize="xs">
                  {t('general.archived')}
                </Badge>
              )}
            </Flex>
            <Text fontSize="xs" color="gray.400" mb={3}>
              {group.ownerName} - {formatDate(Locale.En, group.created)}
            </Text>
            <Flex gap={2} wrap="wrap" align="center">
              {tierTags}
            </Flex>
          </Box>
          <Flex gap={2} align="flex-start">
            {group.archived ? (
              <IconButton
                aria-label={t('general.restore')}
                icon={<Icons.Undo boxSize={4} />}
                size="sm"
                variant="texas-light"
                isLoading={restoring}
                onClick={handleRestore}
              />
            ) : (
              <>
                <Button
                  leftIcon={<Icons.Send boxSize={4} />}
                  size="sm"
                  variant="texas-light"
                  onClick={onMakeOffer}
                >
                  {t('offer.makeOffer')}
                </Button>
                <EditOfferGroupDrawer
                  offerGroupId={group.id}
                  group={group}
                  onUpdated={onUpdated}
                />
                <IconButton
                  aria-label={t('general.archive')}
                  icon={<Icons.Archive boxSize={4} />}
                  size="sm"
                  variant="ghost"
                  isLoading={archiving}
                  onClick={handleArchive}
                />
              </>
            )}
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
}
